import { useState, useRef, useEffect } from "react";
import axios from "axios";
import useConversationScroll from "@/lib/useConversationScroll";
import { Sparkles, X, Send, Loader2, Paperclip, Eraser } from "lucide-react";
import { ZyLogo } from "@/components/ZyLogo";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AssistantActions from "@/components/dashboard/AssistantActions";
import VoiceButton from "@/components/dashboard/VoiceButton";
import AISeesIndicator from "@/components/dashboard/AISeesIndicator";
import AttachmentChip from "@/components/dashboard/AttachmentChip";
import MarkdownMessage from "@/components/dashboard/MarkdownMessage";
import { streamAssistantChat } from "@/lib/aiStream";
import {
  uploadAiFile,
  deleteAiFile,
  validateUpload,
  AI_UPLOAD_ACCEPT_ATTR,
} from "@/lib/aiUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AssistFloating() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm Zynthoro Assist. Ask me anything about the platform or your account.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyAttempt, setHistoryAttempt] = useState(0);
  const [poweredBy, setPoweredBy] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const uploadBusyRef = useRef(false);
  const conversationActionRef = useRef(null);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const conversationScroll = useConversationScroll(scrollRef, contentRef, messages, open, "zynthoro_assist");
  const { user, activeWorkspace } = useAuth();
  const isFounder = !!user?.is_founder;
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wiping, setWiping] = useState(false);

  const wipeMemory = async () => {
    if (conversationActionRef.current || uploadBusyRef.current) return;
    conversationActionRef.current = "wipe";
    setWiping(true);
    try {
      const { data } = await axios.delete(`${API}/ai/memory/zynthoro_assist`, {
        headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
      });
      setSessionId(null);
      conversationScroll.scrollToLatest();
      setMessages([{
        role: "assistant",
        content: "Hi! I'm Zynthoro Assist. Ask me anything about the platform or your account.",
      }]);
      setInput("");
      setAttachments([]);
      toast.success(
        `Memory cleared — ${data.messages_deleted} message${data.messages_deleted === 1 ? "" : "s"} wiped.`
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't clear memory.");
    }
    conversationActionRef.current = null;
    setWiping(false);
    setWipeOpen(false);
  };

  // Resume last conversation when the user opens the panel for the first time
  useEffect(() => {
    if (!open || sessionId) {
      setHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ai/sessions`, {
          params: { assistant: "zynthoro_assist", limit: 1 },
          headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
        });
        if (cancelled) return;
        if (!Array.isArray(data?.sessions)) throw new Error("Incomplete session list.");
        const last = data.sessions[0];
        if (last && (typeof last.session_id !== "string" || !last.session_id)) throw new Error("Incomplete session.");
        if (last && !cancelled) {
          const hist = await axios.get(`${API}/ai/history`, {
            params: { session_id: last.session_id },
            headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
          });
          if (cancelled) return;
          if (!Array.isArray(hist.data?.messages) || hist.data.messages.some((m) =>
            !m || !["user", "assistant"].includes(m.role) || typeof m.content !== "string")) {
            throw new Error("Incomplete conversation history.");
          }
          const msgs = hist.data.messages.map((m) => ({ role: m.role, content: m.content }));
          if (msgs.length) {
            conversationScroll.scrollToLatest();
            setSessionId(last.session_id);
            setMessages(msgs);
          }
        }
      } catch {
        if (!cancelled) setHistoryError("Your previous conversation could not be loaded. Retry before sending another message.");
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, activeWorkspace, user?.id, historyAttempt]);

  const send = async (e, override) => {
    e?.preventDefault?.();
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || busy || historyLoading || historyError || conversationActionRef.current) return;
    if (uploadBusyRef.current || attachments.some((a) => a.status === "uploading")) {
      toast.info("Please wait for the file upload to finish.");
      return;
    }
    conversationActionRef.current = "send";
    setInput("");

    const readyAttachments = attachments.filter((a) => a.status === "ready" && a.file_id);
    const fileIds = readyAttachments.map((a) => a.file_id);
    const bubbleAttachments = readyAttachments.map((a) => ({
      file_id: a.file_id, filename: a.filename, size: a.size,
    }));
    // Retain selected documents until a complete successful response.

    setMessages((m) => [
      ...m,
      { role: "user", content: text, attachments: bubbleAttachments },
      { role: "assistant", content: "", streaming: true },
    ]);
    setBusy(true);

    let localSession = sessionId;
    let hadError = false;

    try {
    await streamAssistantChat({
      expectedWorkspace: activeWorkspace || user?.id,
      assistant: "zynthoro_assist",
      session_id: sessionId || undefined,
      message: text,
      file_ids: fileIds.length ? fileIds : undefined,
      onMeta: (meta) => {
        if (meta?.session_id && !localSession) {
          localSession = meta.session_id;
          setSessionId(meta.session_id);
        }
        if (meta?.badge) setPoweredBy({ badge: meta.badge, provider: meta.provider });
      },
      onDelta: (d) => {
        const chunk = d?.content || "";
        if (!chunk) return;
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, content: (last.content || "") + chunk, streaming: true };
          }
          return next;
        });
      },
      onError: (err) => {
        hadError = true;
        if (err?.code === "AI_CREDIT_LIMIT") {
          toast.error(err?.message || "AI credit limit reached. Buy more to keep working.", {
            action: {
              label: "Buy credits",
              onClick: () => { window.location.href = err?.buy_credits_url || "/dashboard/credits"; },
            },
            duration: 8000,
          });
        } else {
          toast.error(formatApiError(err?.message) || "Assistant is unavailable.");
        }
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && !last.content) {
            const isCreditErr = err?.code === "AI_CREDIT_LIMIT";
            next[next.length - 1] = {
              role: "assistant",
              content: isCreditErr
                ? "You've used all available AI credits. Tap **Buy credits** in the sidebar to top up."
                : "I had trouble responding. Please try again shortly.",
              creditBlocked: isCreditErr,
            };
          }
          return next;
        });
      },
      onDone: () => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
      },
    });

    if (!hadError) {
      const sentIds = new Set(readyAttachments.map((a) => a.local_id));
      setAttachments((current) => current.filter((a) => !sentIds.has(a.local_id)));
    }
    } catch {
      hadError = true;
      toast.error("The response could not be completed. Review the conversation before retrying.");
    } finally {
      conversationActionRef.current = null;
      setBusy(false);
      setMessages((current) => current.map((message) =>
        message.streaming ? { ...message, streaming: false } : message));
      if (hadError) setInput((current) => current || text);
    }
    if (hadError && !localSession) setSessionId(null);
  };

  // ---- Attachment handlers -------------------------------------------------
  const handleAttachClick = () => {
    if (busy || conversationActionRef.current || uploadBusyRef.current) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (busy || conversationActionRef.current || uploadBusyRef.current) return;
    const remaining = Math.max(0, 6 - attachments.length);
    if (!remaining) {
      toast.info("Select no more than six documents per message.");
      return;
    }
    const valid = selected.filter((file) => {
      const error = validateUpload(file);
      if (error) toast.error(error);
      return !error;
    });
    if (valid.length > remaining) {
      toast.info("Only the first documents that fit the six-document limit will be uploaded.");
    }
    uploadBusyRef.current = true;
    try {
      for (const file of valid.slice(0, remaining)) {
        const local_id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setAttachments((prev) => [
          ...prev,
          { local_id, filename: file.name, size: file.size, status: "uploading" },
        ]);
        try {
          const res = await uploadAiFile(file, activeWorkspace || user?.id);
          setAttachments((prev) =>
            prev.map((a) =>
              a.local_id === local_id
                ? { ...a, file_id: res.file_id, size: res.size, status: "ready" }
                : a
            )
          );
        } catch (uploadErr) {
          const msg = formatApiError(uploadErr?.response?.data?.detail) || uploadErr?.message || "Upload failed.";
          toast.error(msg);
          setAttachments((prev) => prev.filter((a) => a.local_id !== local_id));
        }
      }
    } finally {
      uploadBusyRef.current = false;
    }
  };

  const removeAttachment = async (local_id) => {
    if (busy || conversationActionRef.current) return;
    const target = attachments.find((a) => a.local_id === local_id);
    setAttachments((prev) => prev.filter((a) => a.local_id !== local_id));
    if (target?.file_id) deleteAiFile(target.file_id, activeWorkspace || user?.id).catch(() => {});
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="assist-bubble"
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full inline-flex items-center justify-center shadow-2xl transition-all ${open ? "scale-0 opacity-0" : "scale-100 opacity-100"}`}
        style={{ background: "#202B60", color: "#fff" }}
        aria-label="Open Zynthoro Assist"
      >
        <Sparkles size={22} />
      </button>

      <div
        data-testid="assist-panel"
        className={`fixed bottom-6 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[80vh] rounded-2xl shadow-2xl border flex flex-col transition-all ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        style={{ background: "#fff", borderColor: "#202B60" }}
      >
        <div className="px-4 py-3 border-b border-[#eee] flex items-center gap-3" style={{ background: "#0A1628" }}>
          <ZyLogo size={15} />
          <div className="flex-1">
            <p className="text-[12.5px] font-semibold text-white">Zynthoro Assist</p>
            <p className="text-[10.5px] text-white/65">
              {poweredBy?.badge || "Powered by Claude AI"}
            </p>
          </div>
          <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1" aria-label="Close" data-testid="assist-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-[#f1f1f3] bg-white flex justify-end">
          <button
            type="button"
            onClick={() => setWipeOpen(true)}
            disabled={wiping || busy}
            title="Wipe Zynthoro Assist's stored memory for your workspace"
            data-testid="assist-clear-memory-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#0A162820] px-2.5 py-1 text-[11.5px] font-semibold text-[#0A1628] hover:bg-[#F7F8FA] transition-colors disabled:opacity-50"
          >
            {wiping ? <Loader2 size={11} className="animate-spin" /> : <Eraser size={11} />}
            Clear memory
          </button>
        </div>

        <div className="px-4 py-1.5 border-b border-[#f1f1f3] bg-[#FAFAFB]">
          <AISeesIndicator testId="assist-ai-sees" />
        </div>

        {historyLoading && <p role="status" className="px-4 py-2 text-xs text-neutral-600">Loading your previous conversation…</p>}
        {historyError && <div role="alert" className="px-4 py-2 text-xs text-amber-800">
          <p>{historyError}</p>
          <button type="button" onClick={() => setHistoryAttempt((value) => value + 1)}
            className="mt-1 underline" disabled={historyLoading}>Retry conversation</button>
        </div>}
        <div ref={scrollRef} onScroll={conversationScroll.onScroll} onWheel={conversationScroll.onWheel} className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="assist-messages">
          <div ref={contentRef} className="space-y-3.5">
          {messages.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="max-w-[85%] ml-auto flex flex-col items-end gap-1.5">
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 justify-end" data-testid={`assist-msg-${i}-attachments`}>
                    {m.attachments.map((a) => (
                      <AttachmentChip
                        key={a.file_id}
                        filename={a.filename}
                        size={a.size}
                        compact
                        testId={`assist-msg-${i}-attachment-${a.file_id}`}
                      />
                    ))}
                  </div>
                )}
                <div
                  className="text-[13.5px] leading-relaxed px-3.5 py-2.5 rounded-lg rounded-tr-sm text-white whitespace-pre-wrap"
                  style={{ background: "#202B60" }}
                >
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className="flex items-start gap-2 max-w-[92%]">
                <span
                  className="w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0 text-[10px] font-extrabold tracking-[0.04em]"
                  style={{ background: "#0A1628", color: "#D4AF37", border: "1.5px solid #D4AF37" }}
                  aria-label="Zynthoro Assist"
                  data-testid="assist-msg-avatar"
                >
                  Z
                </span>
                <div className="flex flex-col min-w-0">
                  <div className="text-[13.5px] leading-relaxed px-3.5 py-2.5 rounded-lg rounded-tl-sm bg-[#F4F6FB]">
                    <MarkdownMessage>{m.content}</MarkdownMessage>
                    {m.streaming && (
                      <span
                        className="inline-block w-[6px] h-[12px] ml-0.5 align-middle animate-pulse"
                        style={{ background: "#202B60" }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  {!m.streaming && m.content && m.content.length > 10 && (
                    <AssistantActions
                      content={m.content}
                      assistantName="Zynthoro Assist"
                      testIdPrefix={`assist-msg-${i}`}
                    />
                  )}
                </div>
              </div>
            )
          ))}
          {busy && !(messages.length > 0 && messages[messages.length - 1].role === "assistant" && messages[messages.length - 1].streaming) && (
            <div className="flex items-center gap-2">
              <span
                className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-extrabold opacity-80"
                style={{ background: "#0A1628", color: "#D4AF37", border: "1.5px solid #D4AF37" }}
              >
                Z
              </span>
              <div className="flex items-center gap-2 text-[#888] text-[12.5px] bg-[#F4F6FB] px-3.5 py-2 rounded-lg rounded-tl-sm">
                <Loader2 size={13} className="animate-spin" /> Zynthoro Assist is thinking…
              </div>
            </div>
          )}
          </div>
        </div>
        {conversationScroll.showLatest && (
          <button type="button" onClick={conversationScroll.scrollToLatest}
            className="mx-auto my-2 rounded-full border px-4 py-1.5 text-sm">
            Latest messages ↓
          </button>
        )}

        <form onSubmit={send} className="p-3 border-t border-[#eee] flex flex-col gap-2">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="assist-pending-attachments">
              {attachments.map((a) => (
                <AttachmentChip
                  key={a.local_id}
                  filename={a.filename}
                  size={a.size}
                  status={a.status}
                  onRemove={() => removeAttachment(a.local_id)}
                  testId={`assist-pending-${a.local_id}`}
                />
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={AI_UPLOAD_ACCEPT_ATTR}
              multiple
              onChange={handleFileSelected}
              className="hidden"
              data-testid="assist-file-input"
            />
            <button
              type="button"
              onClick={handleAttachClick}
              disabled={busy || wiping}
              title="Attach a file (PDF, DOCX, XLSX, PPTX, CSV — up to 10 MB)"
              aria-label="Attach a file"
              className="shrink-0 h-[38px] w-[38px] inline-flex items-center justify-center rounded-md border border-[#eee] text-[#555] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="assist-attach-btn"
            >
              <Paperclip size={15} />
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Zynthoro anything…"
              data-testid="assist-input"
              className="flex-1 text-[13.5px] outline-none px-3 py-2 rounded-md border border-[#eee] focus:border-[#202B60]"
            />
            <VoiceButton
              testId="assist-voice-btn"
              size={14}
              onInterim={(t) => setInput(t)}
              onFinal={(t) => { setInput(""); send(null, t); }}
            />
            <button type="submit" disabled={busy || wiping || historyLoading || !!historyError || !input.trim()} className="zy-btn-primary px-3 py-2 disabled:opacity-50" data-testid="assist-send">
              <Send size={15} />
            </button>
          </div>
        </form>
      </div>

      <AlertDialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <AlertDialogContent data-testid="assist-clear-memory-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Zynthoro Assist&apos;s memory?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every message Zynthoro Assist has stored for your workspace —
              past sessions, context, everything. Your next conversation will start from a clean slate.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assist-clear-memory-cancel" disabled={wiping || busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="assist-clear-memory-confirm"
              onClick={(e) => { e.preventDefault(); wipeMemory(); }}
              disabled={wiping || busy}
              className="bg-[#B42318] hover:bg-[#8B1A12]"
            >
              {wiping ? "Clearing…" : "Yes, clear memory"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
